import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MobileBuilder } from '../../components/mobile/builder';
import { useBuilderStore } from '../../stores/builderStore';
import { getForm, updateForm } from '../../lib/api';
import type { FormSchema } from '../../types/form';

export default function MobileFormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { initializeForm, getSchema, markAsSaved, hasUnsavedChanges } = useBuilderStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  useEffect(() => {
    async function loadForm() {
      if (!id) {
        setError('Form ID is required');
        setLoading(false);
        return;
      }

      try {
        const form = await getForm(id);
        if (form) {
          initializeForm(
            form.id,
            form.name,
            form.schema as FormSchema
          );
          setFormName(form.name);
        } else {
          setError('Form not found');
        }
      } catch (err) {
        console.error('Failed to load form:', err);
        setError('Failed to load form');
      } finally {
        setLoading(false);
      }
    }

    loadForm();
  }, [id, initializeForm]);

  const handleSave = async () => {
    if (!id) return;

    try {
      const schema = getSchema();
      await updateForm(id, { schema });
      markAsSaved();
    } catch (err) {
      console.error('Failed to save form:', err);
      throw err;
    }
  };

  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      navigate('/m/forms');
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmLeave = useCallback(() => {
    setShowUnsavedWarning(false);
    navigate('/m/forms');
  }, [navigate]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" role="status" aria-label="Loading form" />
          <p className="text-gray-500 dark:text-gray-400">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileBuilder
        formId={id}
        formName={formName}
        onSave={handleSave}
        onBack={handleBack}
      />

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setShowUnsavedWarning(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-scaleIn">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Unsaved Changes
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                You have unsaved changes. Are you sure you want to leave?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUnsavedWarning(false)}
                  className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium touch-manipulation"
                >
                  Keep Editing
                </button>
                <button
                  onClick={confirmLeave}
                  className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium touch-manipulation"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
